import { useEffect, useRef, useState } from "react";

const CHARS = "abcdefghijklmnopqrstuvwxyz0123456789!@#$%&*<>/\\|:;_-+=?";

/**
 * "Decrypting" text effect — renders each character as a random glyph
 * and settles on the real letters one index at a time. Matches the vibe
 * of a terminal boot sequence / vault reveal.
 *
 * Durations measured in frames (~16ms each). Total runtime ≈ duration ms.
 */
export function ScrambleText({
  text,
  className = "",
  duration = 800,
  delay = 0,
  revealEach = 40,
}: {
  text: string;
  className?: string;
  duration?: number;
  delay?: number;
  revealEach?: number;
}) {
  const [display, setDisplay] = useState(text);
  const frameRef = useRef<number>(0);
  const startRef = useRef<number>(0);
  const revealIdxRef = useRef<number>(0);

  useEffect(() => {
    let raf: number;
    let timeoutId: number;

    function tick(ts: number) {
      if (!startRef.current) startRef.current = ts;
      const elapsed = ts - startRef.current;
      revealIdxRef.current = Math.min(text.length, Math.floor(elapsed / revealEach));

      const out = text
        .split("")
        .map((ch, i) => {
          if (ch === " ") return " ";
          if (i < revealIdxRef.current) return ch;
          return CHARS[Math.floor(Math.random() * CHARS.length)];
        })
        .join("");

      setDisplay(out);
      frameRef.current++;
      if (elapsed < duration) raf = requestAnimationFrame(tick);
      else setDisplay(text);
    }

    if (delay > 0) {
      timeoutId = window.setTimeout(() => {
        raf = requestAnimationFrame(tick);
      }, delay);
    } else {
      raf = requestAnimationFrame(tick);
    }
    return () => {
      if (raf) cancelAnimationFrame(raf);
      if (timeoutId) clearTimeout(timeoutId);
    };
  }, [text, duration, delay, revealEach]);

  return (
    <span className={className} style={{ fontVariantLigatures: "none" }}>
      {display}
    </span>
  );
}
